// Browser transport shared by the inbox and conversation screens.
function createChatRealtimeClient({ requestTicket, refresh, onError, window: browser = window }) {
  let socket = null;
  let timer = null;
  let deadline = null;
  let attempt = 0;
  let generation = 0;
  let stopped = false;
  let refreshing = false;
  let pending = false;
  let pendingChanges = [];
  let refreshTimer = null;
  let refreshFailures = 0;
  let reported = false;
  const active = () => !stopped && !browser.document.hidden && browser.navigator.onLine !== false;

  function error(value) {
    const status = Number(value && value.response && (value.response.data && value.response.data.status || value.response.status));
    const auth = [401, 403, 409].includes(status);
    if (auth) stop();
    if (!reported || auth) { reported = true; onError(value, auth); }
  }

  async function invalidate(changes = null) {
    if (!Array.isArray(changes) || changes.length > 30 || changes.some((item) => !item || !/^[a-f\d]{24}$/i.test(item.id) || !["message", "read"].includes(item.kind))) pendingChanges = null;
    else if (pendingChanges !== null) {
      for (const item of changes) {
        const previous = pendingChanges.find((entry) => entry.id === item.id);
        if (!previous) pendingChanges.push({ ...item });
        else {
          previous.messageVersion = Math.max(previous.messageVersion || 0, item.messageVersion || 0);
          if (item.kind === "message") previous.kind = "message";
        }
      }
      if (pendingChanges.length > 30) pendingChanges = null;
    }
    pending = true;
    if (refreshing || !active()) return;
    refreshing = true;
    try {
      while (pending && active()) {
        pending = false;
        const batch = pendingChanges;
        pendingChanges = [];
        await refresh(batch);
        refreshFailures = 0;
      }
    } catch (value) {
      error(value);
      if (active() && refreshFailures++ < 3 && !refreshTimer) {
        refreshTimer = browser.setTimeout(() => { refreshTimer = null; void invalidate(); }, 1000 * refreshFailures);
      } else if (active() && !refreshTimer) {
        retry(); // recover the last missed invalidation even if no more messages arrive
      }
    } finally { refreshing = false; }
  }

  function disconnect() {
    generation++;
    browser.clearTimeout(timer);
    browser.clearTimeout(deadline);
    timer = deadline = null;
    if (socket) {
      socket.onclose = socket.onmessage = socket.onerror = socket.onopen = null;
      socket.close();
      socket = null;
    }
  }

  function retry() {
    disconnect();
    if (!active()) return;
    const wait = Math.min(30000, 1000 * Math.pow(2, Math.min(attempt++, 5))) + Math.floor(Math.random() * 500);
    timer = browser.setTimeout(connect, wait);
  }

  async function connect() {
    timer = null;
    if (!active() || socket) return;
    const current = ++generation;
    try {
      const data = await requestTicket();
      if (!active() || current !== generation) return;
      const scheme = browser.location.protocol === "https:" ? "wss:" : "ws:";
      socket = new browser.WebSocket(scheme + "//" + browser.location.host + data.path);
      deadline = browser.setTimeout(retry, 10000);
      socket.onopen = () => socket.send(JSON.stringify({ ticket: data.ticket }));
      socket.onmessage = (event) => {
        let message;
        try { message = JSON.parse(event.data); } catch (_) { return; }
        if (!message || typeof message !== "object") return;
        if (message.type === "ready") {
          browser.clearTimeout(deadline);
          deadline = null;
          attempt = 0;
          reported = false;
          void invalidate(); // snapshot after subscription also closes the initial race
        } else if (message.type === "changed") void invalidate(message.changes);
      };
      socket.onclose = retry; // including a ticket consumed by a previous deployment slot
      socket.onerror = () => {};
    } catch (value) {
      if (current !== generation || stopped) return;
      error(value);
      retry();
    }
  }

  function visibility() {
    disconnect();
    if (active()) { attempt = 0; void invalidate(); void connect(); }
  }

  function storage(event) {
    if (event.key === "accesstoken" || event.key === "isLoggedIn") {
      if (!event.newValue) stop();
      else visibility();
    }
  }

  function stop() {
    stopped = true;
    pending = false;
    disconnect();
    browser.clearTimeout(refreshTimer);
    browser.document.removeEventListener("visibilitychange", visibility);
    browser.removeEventListener("online", visibility);
    browser.removeEventListener("offline", visibility);
    browser.removeEventListener("storage", storage);
    browser.removeEventListener("pagehide", disconnect);
    browser.removeEventListener("pageshow", visibility);
  }

  browser.document.addEventListener("visibilitychange", visibility);
  browser.addEventListener("online", visibility);
  browser.addEventListener("offline", visibility);
  browser.addEventListener("storage", storage);
  browser.addEventListener("pagehide", disconnect);
  browser.addEventListener("pageshow", visibility);
  void invalidate();
  void connect();
  return { stop, refresh: invalidate };
}

function chatResponse(response) {
  const payload = response && response.data;
  if (!payload || payload.status !== 200 || payload.success === false) {
    const error = new Error(payload && payload.message || "채팅 정보를 불러오지 못했습니다.");
    error.response = response;
    throw error;
  }
  return payload;
}

module.exports = { createChatRealtimeClient, chatResponse };
